/**
 * How far the pictures of the open canvas have loaded, for the loading bar in the top bar
 * (components/MediaLoadingBar.tsx). On the website a workspace's pictures arrive one by one
 * from the cloud over many seconds; the bar says that they are coming, and when they all are
 * there.
 *
 * Loading happens in rounds: a round starts when something is loading and ends when nothing
 * is. A picture counts in two steps: its lookup (hooks/useFileUrl.ts asks where its bytes are)
 * and its `<img>` (the bytes arriving). Only what was loading at some point in the round counts,
 * so a workspace whose pictures are all there shows no bar when one more is added, and the bar
 * only moves forwards: a finished lookup adds an `<img>` to the round and finishes a step, and
 * (done + 1) / (total + 1) is never less than done / total.
 */

/** Pending lookups of pictures in the canvas (hooks/useFileUrl.ts counts into it) */
export interface MediaLookups {
    /** `count` lookups started; the function returned ends them (answered, or their view went away) */
    start(count?: number): () => void;
    /** Lookups started and ended since the round began */
    counts(): { started: number; ended: number };
    /** A new round: what is still pending is all that counts */
    restart(): void;
    subscribe(listener: () => void): () => void;
}

export const createMediaLookups = (): MediaLookups => {
    let started = 0;
    let ended = 0;
    const listeners = new Set<() => void>();
    const changed = () => listeners.forEach(listener => listener());
    return {
        start(count = 1) {
            if (count <= 0) return () => undefined;
            started += count;
            changed();
            let open = true;
            return () => {
                if (!open) return;
                open = false;
                ended += count;
                changed();
            };
        },
        counts: () => ({ started, ended }),
        restart() {
            started -= ended;
            ended = 0;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
    };
};

/** What is loading in the canvas right now, read from its content layer */
export interface LoadingNow {
    /** `<img>` elements whose bytes have not arrived */
    images: Element[];
    /** Far-view stand-ins still waiting for their small picture (only while zoomed out far) */
    waiting: Element[];
}

/** The `<img>` elements still loading in `layer`, and, when zoomed out far, the far-view stand-ins still waiting */
export const readLoading = (layer: ParentNode, far: boolean): LoadingNow => ({
    images: Array.from(layer.querySelectorAll('img')).filter(img => !(img as HTMLImageElement).complete),
    waiting: far ? Array.from(layer.querySelectorAll('[data-far-picture="waiting"]')) : [],
});

/** One loading round: everything that was loading in it at some point */
export interface LoadingRound {
    startedAt: number;
    images: Set<Element>;
    waiting: Set<Element>;
}

export const startRound = (now: number): LoadingRound => ({ startedAt: now, images: new Set(), waiting: new Set() });

const imageDone = (img: Element) => !img.isConnected || (img as HTMLImageElement).complete;
const waitingDone = (far: boolean) => (el: Element) => !far || !el.isConnected || el.getAttribute('data-far-picture') !== 'waiting';
const countWhere = (items: Set<Element>, done: (el: Element) => boolean) => {
    let n = 0;
    items.forEach(el => { if (done(el)) n++; });
    return n;
};

/**
 * Adds what is loading now to the round and says how far it is: `done` of `total` steps, and
 * whether anything still loads. Errors count as done: a picture that cannot load is not waited for.
 */
export const measureRound = (
    round: LoadingRound,
    now: LoadingNow,
    lookups: { started: number; ended: number },
    far: boolean,
): { done: number; total: number; pending: boolean } => {
    now.images.forEach(img => round.images.add(img));
    now.waiting.forEach(el => round.waiting.add(el));
    const total = lookups.started + round.images.size + round.waiting.size;
    const done = lookups.ended + countWhere(round.images, imageDone) + countWhere(round.waiting, waitingDone(far));
    return { done, total, pending: done < total };
};
